/**
 * Curated experiments shown beneath the mixing station.
 * Metadata fuels the experiment cards: difficulty, predicted observation,
 * estimated time, and an optional safety note for dramatic reactions.
 */
export type ExperimentDifficulty = "beginner" | "intermediate" | "advanced";

export interface SuggestedExperiment {
  label: string;
  hint: string;
  formulas: string[];
  difficulty: ExperimentDifficulty;
  /** One-line preview of what the student will observe. */
  observation: string;
  /** Realistic time to set up + watch the reaction, in minutes. */
  estimatedMinutes: number;
  /** Surfaces a red warning chip on the card when present. */
  safetyNote?: string;
  /** Optional grouping for the section heading. */
  group: "popular" | "more";
}

export const suggestedExperiments: SuggestedExperiment[] = [
  {
    label: "Sodium + Water",
    hint: "Na + H₂O → ?",
    formulas: ["Na", "H₂O"],
    difficulty: "advanced",
    observation: "Fizzes, skates across the surface, may ignite with a yellow flame.",
    estimatedMinutes: 2,
    safetyNote: "Highly exothermic - only a tiny piece, behind a safety screen.",
    group: "popular",
  },
  {
    label: "Baking Soda + Vinegar",
    hint: "NaHCO₃ + CH₃COOH → ?",
    formulas: ["NaHCO₃", "CH₃COOH"],
    difficulty: "beginner",
    observation: "Vigorous fizzing as CO₂ gas is released.",
    estimatedMinutes: 3,
    group: "popular",
  },
  {
    label: "Golden Rain",
    hint: "Pb(NO₃)₂ + KI → ?",
    formulas: ["Pb(NO₃)₂", "KI"],
    difficulty: "intermediate",
    observation: "Brilliant golden-yellow lead iodide precipitate forms.",
    estimatedMinutes: 5,
    safetyNote: "Lead salts are toxic - handle with gloves; dispose properly.",
    group: "popular",
  },
  {
    label: "Rust Formation",
    hint: "Fe + O₂ → ?",
    formulas: ["Fe", "O₂"],
    difficulty: "beginner",
    observation: "Slow reddish-brown coating forms on the iron surface.",
    estimatedMinutes: 10,
    group: "popular",
  },
  {
    label: "Acid-Base Neutralization",
    hint: "HCl + NaOH → ?",
    formulas: ["HCl", "NaOH"],
    difficulty: "beginner",
    observation: "Clear solution warms slightly; pH shifts to neutral.",
    estimatedMinutes: 3,
    safetyNote: "Both reactants are corrosive - wear goggles.",
    group: "popular",
  },
  {
    label: "Elephant Toothpaste",
    hint: "H₂O₂ → ?",
    formulas: ["H₂O₂"],
    difficulty: "intermediate",
    observation: "Rapid foaming column of oxygen-rich foam erupts.",
    estimatedMinutes: 4,
    group: "popular",
  },
  {
    label: "Burning Magnesium",
    hint: "Mg + O₂ → ?",
    formulas: ["Mg", "O₂"],
    difficulty: "intermediate",
    observation: "Blinding white flame; leaves a fine white powder.",
    estimatedMinutes: 2,
    safetyNote: "Do not look directly - use welding glasses.",
    group: "more",
  },
  {
    label: "Zinc + Acid",
    hint: "Zn + HCl → ?",
    formulas: ["Zn", "HCl"],
    difficulty: "beginner",
    observation: "Steady stream of hydrogen bubbles from the metal.",
    estimatedMinutes: 4,
    group: "more",
  },
  {
    label: "Silver Precipitate",
    hint: "AgNO₃ + NaCl → ?",
    formulas: ["AgNO₃", "NaCl"],
    difficulty: "beginner",
    observation: "Cloudy white silver chloride precipitate forms instantly.",
    estimatedMinutes: 2,
    group: "more",
  },
  {
    label: "Copper + Silver Nitrate",
    hint: "Cu + AgNO₃ → ?",
    formulas: ["Cu", "AgNO₃"],
    difficulty: "intermediate",
    observation: "Silvery crystals grow on copper; solution turns blue.",
    estimatedMinutes: 8,
    group: "more",
  },
  {
    label: "Potassium + Water",
    hint: "K + H₂O → ?",
    formulas: ["K", "H₂O"],
    difficulty: "advanced",
    observation: "Lilac flame and rapid hydrogen release; faster than sodium.",
    estimatedMinutes: 2,
    safetyNote: "Even more reactive than sodium - use safety screen.",
    group: "more",
  },
  {
    label: "Methane Combustion",
    hint: "CH₄ + O₂ → ?",
    formulas: ["CH₄", "O₂"],
    difficulty: "intermediate",
    observation: "Pale blue flame; produces water vapor and CO₂.",
    estimatedMinutes: 2,
    safetyNote: "Flammable gas - keep ignition controlled.",
    group: "more",
  },
  {
    label: "Limestone Decomposition",
    hint: "CaCO₃ → ?",
    formulas: ["CaCO₃"],
    difficulty: "intermediate",
    observation: "On strong heating, releases CO₂ leaving lime (CaO).",
    estimatedMinutes: 6,
    group: "more",
  },
  {
    label: "Calcium + Water",
    hint: "Ca + H₂O → ?",
    formulas: ["Ca", "H₂O"],
    difficulty: "intermediate",
    observation: "Steady fizzing; cloudy lime water and hydrogen produced.",
    estimatedMinutes: 3,
    group: "more",
  },
  {
    label: "Making Table Salt",
    hint: "Na + Cl₂ → ?",
    formulas: ["Na", "Cl₂"],
    difficulty: "advanced",
    observation: "Bright yellow flame; white sodium chloride crystals form.",
    estimatedMinutes: 3,
    safetyNote: "Chlorine gas is toxic; sodium reactive - fume hood only.",
    group: "more",
  },
  {
    label: "Thermite Reaction",
    hint: "Al + Fe₂O₃ → ?",
    formulas: ["Al", "Fe₂O₃"],
    difficulty: "advanced",
    observation: "Blinding white sparks and molten iron drip from the crucible.",
    estimatedMinutes: 4,
    safetyNote: "Reaches ~2500 °C - outdoor demo, sand bucket, full PPE.",
    group: "more",
  },
  {
    label: "Iron in Copper Sulfate",
    hint: "Fe + CuSO₄ → ?",
    formulas: ["Fe", "CuSO₄"],
    difficulty: "beginner",
    observation: "Blue solution fades to pale green; the nail turns coppery.",
    estimatedMinutes: 6,
    group: "more",
  },
  {
    label: "Barium Sulfate Precipitate",
    hint: "BaCl₂ + Na₂SO₄ → ?",
    formulas: ["BaCl₂", "Na₂SO₄"],
    difficulty: "beginner",
    observation: "Dense milky-white precipitate appears the instant they mix.",
    estimatedMinutes: 3,
    safetyNote: "Soluble barium salts are toxic - gloves and goggles.",
    group: "more",
  },
  {
    label: "Ammonia Smoke Ring",
    hint: "NH₃ + HCl → ?",
    formulas: ["NH₃", "HCl"],
    difficulty: "intermediate",
    observation: "Thick white smoke of ammonium chloride forms in mid-air.",
    estimatedMinutes: 3,
    safetyNote: "Both gases are pungent and corrosive - fume hood only.",
    group: "more",
  },
  {
    label: "Black Snake (Sugar + H₂SO₄)",
    hint: "C₁₂H₂₂O₁₁ + H₂SO₄ → ?",
    formulas: ["C₁₂H₂₂O₁₁", "H₂SO₄"],
    difficulty: "advanced",
    observation: "Sugar darkens and a steaming black column of carbon rises out.",
    estimatedMinutes: 5,
    safetyNote: "Concentrated H₂SO₄ causes severe burns - full PPE, fume hood.",
    group: "more",
  },
];
